import { TCAppConfig } from "../../../../../../shared/app-configs/tc-app-configs";
import { IncomingTCInformation, IncomingProductNetWeightInfo } from "../../../tc-models/tc-req-dto/incoming-details.req.dto";

export interface IncomingTcEntry {
  id: number;
  incomingTcId?: string;
  productId?: string;
  incomingTcInfo: IncomingTCInformation;
  productNetWeight: IncomingProductNetWeightInfo;
  inputTcStandard?: Array<{
    incomingProductArticleNo: string;
    incomingProductStandard: string[];
    incomingTcStandardDescription?: string[];
  }>;
}

export interface IncomingTcPayload {
  tcId: string;
  incomingTcInfo: IncomingTCInformation;
  productNetWeight: IncomingProductNetWeightInfo;
}

const normalizeStringArray = (value: any): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item === undefined || item === null) return "";
        return String(item).trim();
      })
      .filter((val) => Boolean(val));
  }

  if (value === undefined || value === null) return [];

  if (typeof value === "string" && value.includes(",")) {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter((val) => Boolean(val));
  }

  const normalized = String(value).trim();
  return normalized ? [normalized] : [];
};

const uniqueStrings = (values: string[]): string[] => {
  return Array.from(new Set(values.map((val) => String(val).trim()).filter((val) => Boolean(val))));
};

export async function fetchIncomingTcDetailsList(
  executeGet: (url: string, params?: Record<string, any>, config?: Record<string, any>) => Promise<any>,
  tcId: string,
  limit: number = 10,
  offset: number = 0
): Promise<{ entries: IncomingTcEntry[]; totalCount: number }> {
  try {
    const response = await executeGet(TCAppConfig.GET_INCOMING_DETAIL_LIST_V4, { 
      tcId,
      limit,
      offset
    });
    const data = response?.data?.response || response?.response || [];
    const totalCount = response?.data?.no_of_records || response?.no_of_records || data.length;

    const isAtcValue = (val: any) => {
      if (!val) return false;
      try { return String(val).toUpperCase().startsWith('GOTS') || String(val).toUpperCase().startsWith('ATU'); } catch { return false; }
    };
    const isRmdfValue = (val: any) => {
      if (!val) return false;
      try { return String(val).toUpperCase().startsWith('RMDF-') || String(val).toUpperCase().startsWith('RMDF'); } catch { return false; }
    };

    const mappedData = data.map((item: any, index: number) => {
      // Handle nested structure: if item has incomingTcProductInformation array, use first element
      const baseItem = Array.isArray(item.incomingTcProductInformation) && item.incomingTcProductInformation.length > 0
        ? { ...item, ...item.incomingTcProductInformation[0] }
        : item;

      const incomingTcId = item.incomingTcId || item.incoming_tc_id || item.id || undefined;
      const productId = item.productId || item.product_id || item.tcProductId || undefined;

      const rawIncomingNo = baseItem.tcProductIncomingTcNo || baseItem.incomingTcNo || baseItem.incoming_tc_no || '';
      const rawAtcNo = baseItem.incomingAtcNo || baseItem.incoming_atc_no || '';
      const rawRmdfNo = baseItem.incomingRmdfNo || baseItem.incoming_rmdf_no || '';

      const incomingIsAtc = isAtcValue(rawIncomingNo);
      const incomingIsRmdf = isRmdfValue(rawIncomingNo);

      const tcIncomingATCNo = incomingIsAtc ? String(rawIncomingNo) : (rawAtcNo || '');
      const tcIncomingRMDFNo = incomingIsRmdf ? String(rawIncomingNo) : (rawRmdfNo || '');
      const tcIncomingTCNo = (!incomingIsAtc && !incomingIsRmdf) ? (rawIncomingNo || '') : '';

      const weightInfoList = baseItem.incomingTcProductNetWeightUsedInformation || baseItem.inputTcProductNetWeightUsedInformation || [];

      const weightBasedStandards = Array.isArray(weightInfoList)
        ? weightInfoList.map((weightInfo: any) => ({
            incomingProductArticleNo: weightInfo.tcProductIncomingArticleNo || weightInfo.incomingArticleNo || weightInfo.articleNo || "",
            incomingProductStandard: normalizeStringArray(weightInfo.tcProductIncomingTcStandard || weightInfo.incomingProductStandard || weightInfo.incoming_tc_standard),
            incomingTcStandardDescription: normalizeStringArray(weightInfo.tcProductIncomingTcStandard || weightInfo.incomingTcStandardDescription || weightInfo.incoming_tc_standard_description),
          })).filter((entry) => Boolean(entry.incomingProductArticleNo))
        : [];

      const legacyInputTcStandardList = item.inputTcStandard || item.input_tc_standard || [];
      const legacyStandards = Array.isArray(legacyInputTcStandardList)
        ? legacyInputTcStandardList.map((stdItem: any) => {
            // Handle case where inputTcStandard is a simple array of strings like ["GRS"]
            if (typeof stdItem === 'string') {
              return {
                incomingProductArticleNo: baseItem.tcAssociatedProductArticleNo || baseItem.tc_product_article_no || "",
                incomingProductStandard: normalizeStringArray([stdItem]),
                incomingTcStandardDescription: normalizeStringArray([stdItem]),
              };
            }
            // Handle case where inputTcStandard is an array of objects
            return {
              incomingProductArticleNo: stdItem.incomingProductArticleNo || stdItem.incoming_product_article_no || "",
              incomingProductStandard: normalizeStringArray(stdItem.incomingProductStandard || stdItem.incoming_product_standard),
              incomingTcStandardDescription: normalizeStringArray(stdItem.incomingTcStandardDescription || stdItem.incoming_tc_standard_description),
            };
          }).filter((entry) => Boolean(entry.incomingProductArticleNo))
        : [];

      const inputTcStandard = weightBasedStandards.length > 0 ? weightBasedStandards : legacyStandards;
      const productArticlesFromStandards = uniqueStrings(
        inputTcStandard.map((entry) => entry.incomingProductArticleNo || "")
      );

      // Extract article numbers from weight info if not already found
      if (!productArticlesFromStandards.length && Array.isArray(weightInfoList)) {
        weightInfoList.forEach((weightInfo: any) => {
          const articleNo = weightInfo.tcProductIncomingArticleNo || weightInfo.incomingArticleNo || weightInfo.articleNo;
          if (articleNo && !productArticlesFromStandards.includes(String(articleNo))) {
            productArticlesFromStandards.push(String(articleNo));
          }
        });
      }

      const incomingProductNoRaw = baseItem.incomingProductArticleNo || baseItem.incoming_product_article_no || baseItem.incomingArticleNo || null;
      let incomingProductNo: string[] = productArticlesFromStandards.length > 0 ? productArticlesFromStandards : [];

      if (!incomingProductNo.length) {
        if (Array.isArray(incomingProductNoRaw)) {
          incomingProductNo = incomingProductNoRaw.map((value: any) => String(value));
        } else if (incomingProductNoRaw) {
          incomingProductNo = [String(incomingProductNoRaw)];
        }
      }

      const aggregatedStandards = uniqueStrings(
        inputTcStandard.reduce<string[]>((acc, entry) => {
          if (Array.isArray(entry.incomingProductStandard)) {
            acc.push(...entry.incomingProductStandard);
          }
          return acc;
        }, [])
      );

      return {
        id: index,
        incomingTcId,
        productId,
        incomingTcInfo: {
          tcAssociatedProductArticleNo: baseItem.tcAssociatedProductArticleNo || baseItem.tc_product_article_no || baseItem.tcProductArticleNo || "",
          tcIncomingTCNo,
          tcIncomingATCNo,
          tcIncomingRMDFNo,
          incomingTcLicensingCode: baseItem.incomingTcLicensingCode || "",
          tcIncomingTCStandard: aggregatedStandards.length > 0
            ? aggregatedStandards
            : normalizeStringArray(baseItem.incomingTcStandard || baseItem.incoming_tc_standard),
          tcIncomingTCStandardDescription: normalizeStringArray(
            baseItem.incomingTcStandardDescription || baseItem.incoming_tc_standard_description || aggregatedStandards
          ),
          tcIncomingProductNo: incomingProductNo,
          tcIncomingTcId: baseItem.incomingTcNoId || baseItem.incoming_tc_no_id || incomingTcId || "",
          tcIncomingAtcId: "",
          tcIncomingLedgerTransactionId: "",
          isFlagSeller: (baseItem.isSellerFarm === 'Yes' || baseItem.isSeller === 'Yes' || baseItem.isFlagSeller === 'Yes') ? 'Yes' : 'No',
        },
        productNetWeight: {
          netWeights: {},
          rawMaterials: {},
        },
        inputTcStandard,
      } as IncomingTcEntry;
    });

    return {
      entries: mappedData,
      totalCount
    };
  } catch (err: any) {
    console.error("Error fetching incoming TC details list:", err);

    // If backend signals "no records" in an error response, treat it as an empty list
    const noRecords =
      err?.response?.data?.no_of_records === 0 ||
      (Array.isArray(err?.response?.data?.response) && err.response.data.response.length === 0);

    if (noRecords) {
      return {
        entries: [],
        totalCount: 0
      };
    }

    throw new Error("Failed to load incoming TC details list.");
  }
}


export async function fetchIncomingTcDetail(
    executeGet: (url: string, params?: Record<string, any>, config?: Record<string, any>) => Promise<any>,
    tcId: string,
    incomingTcId: string,
    productId: string
  ): Promise<IncomingTcEntry | null> {

    if (!incomingTcId && !productId) return null;
  
    try {
       
        const params: Record<string, any> = { tcId };
        if (productId) params.productId = productId;
        const config: Record<string, any> = {};

        let effectiveIncomingTcId = incomingTcId;
        if (!effectiveIncomingTcId && productId) {
          try {
            const listResp = await executeGet(TCAppConfig.GET_INCOMING_DETAIL_LIST_V4, { tcId });
            const listData: any[] = listResp?.data?.response || listResp?.response || [];
       
            const found = listData.find((it: any) => {
              const pid = it.productId || it.product_id || it.tcProductId || it.tc_product_id;
              if (pid && pid === productId) return true;
              const assocArticle = it.tcAssociatedProductArticleNo || it.tc_associated_product_article_no || it.incomingProductArticleNo || it.incoming_product_article_no;
              if (assocArticle && (Array.isArray(assocArticle) ? assocArticle.includes(productId) : assocArticle === productId)) return true;
              const incomingNo = it.incomingTcNo || it.incoming_tc_no;
              if (incomingNo && incomingNo === productId) return true;
              return false;
            });
            if (found) {
              const incomingKey = Object.keys(found).find(k => k && k.toString().trim().toLowerCase() === 'incomingtcid');
              effectiveIncomingTcId = (incomingKey ? found[incomingKey] : undefined) || found.incomingTcId || found.incoming_tc_id || found.id || found.incomingId || undefined;
              if (effectiveIncomingTcId && typeof effectiveIncomingTcId === 'string') {
                effectiveIncomingTcId = effectiveIncomingTcId.trim();
              }
            }
          } catch (e) {
            // ignore fallback discovery errors
          }
        }

        if (effectiveIncomingTcId) {
       
          config.headers = { incomingTcId: effectiveIncomingTcId };
    
          params.incomingTcId = effectiveIncomingTcId;
        }

        const response = await executeGet(TCAppConfig.GET_INCOMING_DETAIL_V4, params, config);
  
 
      const respData = response?.data || response;


      const incomingInfoArr =
        respData?.incomingTcProductInformation ||
        respData?.inputTcInformation ||
        respData?.data?.inputTcInformation ||
        respData?.incoming_tc_information ||
        respData?.incomingTcInformationList ||
        null;

      if (!incomingInfoArr || incomingInfoArr.length === 0) return null;

  const tcInfo = incomingInfoArr[0];
  
      const netWeights: Record<string, number> = {};
      const rawMaterials: Record<
        string,
        Array<{
          tcProductCategoryCode: string;
          tcProductDetailCode: string;
          rawMaterialCode: string;
          rawMaterialPercentage: number;
          rawMaterialWeight: number;
        }>
      > = {};
      const standardsByArticle: Record<string, string[]> = {};

      const weightInfoList =
        tcInfo.incomingTcProductNetWeightUsedInformation ||
        tcInfo.inputTcProductNetWeightUsedInformation ||
        tcInfo.incoming_tc_product_net_weight_used_information ||
        tcInfo.incomingTcProductNetWeight ||
        null;

      let derivedInputTcStandard: Array<{
        incomingProductArticleNo: string;
        incomingProductStandard: string[];
        incomingTcStandardDescription: string[];
      }> = [];

      if (Array.isArray(weightInfoList)) {
        derivedInputTcStandard = weightInfoList
          .map((weightInfo: any) => {
            const articleNo = weightInfo.tcProductIncomingArticleNo || weightInfo.incomingArticleNo || weightInfo.articleNo || "";
            if (!articleNo) return null;

            const rawNetWeight =
              weightInfo.tcProductIncomingArticleNetWeightUsed ??
              weightInfo.incomingTcProductNetWeightUsed ??
              weightInfo.incoming_tc_product_net_weight_used ??
              weightInfo.incomingTcProductNetWeight ??
              0;
            const numericWeight = Number(rawNetWeight) || 0;
            if (numericWeight > 0) {
              netWeights[articleNo] = numericWeight;
            }

            const rawList =
              weightInfo.tcProductIncomingRawMaterialInformation ||
              weightInfo.incomingTcRawMaterialInformation ||
              weightInfo.incoming_tc_raw_material_information ||
              weightInfo.incomingTcRawMaterial ||
              weightInfo.rawMaterials ||
              [];

            if (Array.isArray(rawList) && rawList.length > 0) {
              rawMaterials[articleNo] = rawList.map((rm: any) => ({
                tcProductCategoryCode: rm.tcProductCategoryCode || rm.tc_product_category_code || "",
                tcProductDetailCode: rm.tcProductDetailCode || rm.tc_product_detail_code || "",
                rawMaterialCode: rm.rawMaterialCode || rm.raw_material_code || rm.code || "",
                rawMaterialPercentage: Number(rm.rawMaterialPercentage || rm.raw_material_percentage || 0),
                rawMaterialWeight: Number(rm.rawMaterialWeight || rm.raw_material_weight || 0),
              }));
            } else {
              rawMaterials[articleNo] = [];
            }

            const standardsForArticle = normalizeStringArray(
              weightInfo.tcProductIncomingTcStandard ||
              weightInfo.incomingProductStandard ||
              weightInfo.incoming_tc_standard
            );

            if (standardsForArticle.length > 0) {
              standardsByArticle[articleNo] = standardsForArticle;
            }

            return {
              incomingProductArticleNo: articleNo,
              incomingProductStandard: standardsForArticle,
              incomingTcStandardDescription: normalizeStringArray(
                weightInfo.tcProductIncomingTcStandard ||
                weightInfo.incomingTcStandardDescription ||
                weightInfo.incoming_tc_standard_description ||
                standardsForArticle
              ),
            };
          })
          .filter(
            (entry): entry is {
              incomingProductArticleNo: string;
              incomingProductStandard: string[];
              incomingTcStandardDescription: string[];
            } => Boolean(entry && entry.incomingProductArticleNo)
          );
      }

      if (!derivedInputTcStandard.length) {
        const legacyInputTcStandardList = tcInfo.inputTcStandard || tcInfo.input_tc_standard || [];
        derivedInputTcStandard = Array.isArray(legacyInputTcStandardList)
          ? legacyInputTcStandardList
              .map((item: any) => ({
                incomingProductArticleNo: item.incomingProductArticleNo || item.incoming_product_article_no || "",
                incomingProductStandard: normalizeStringArray(item.incomingProductStandard || item.incoming_product_standard),
                incomingTcStandardDescription: normalizeStringArray(item.incomingTcStandardDescription || item.incoming_tc_standard_description),
              }))
              .filter((entry) => Boolean(entry.incomingProductArticleNo))
          : [];
      }

      if (!Object.keys(standardsByArticle).length && derivedInputTcStandard.length > 0) {
        derivedInputTcStandard.forEach((entry) => {
          if (entry.incomingProductArticleNo) {
            standardsByArticle[entry.incomingProductArticleNo] = entry.incomingProductStandard || [];
          }
        });
      }

      const articleSet = new Set<string>();
      Object.keys(netWeights).forEach((article) => articleSet.add(article));
      Object.keys(standardsByArticle).forEach((article) => articleSet.add(article));
      derivedInputTcStandard.forEach((entry) => {
        if (entry.incomingProductArticleNo) {
          articleSet.add(entry.incomingProductArticleNo);
        }
      });

      let incomingProductArticleNoArr: string[] = Array.from(articleSet);

      if (!incomingProductArticleNoArr.length) {
        const incomingProductArticleNoRaw =
          tcInfo.incomingProductArticleNo ||
          tcInfo.incoming_product_article_no ||
          tcInfo.incomingProductArticleNoList ||
          tcInfo.incomingArticleNo ||
          null;

        if (Array.isArray(incomingProductArticleNoRaw)) {
          incomingProductArticleNoRaw.forEach((x: any) => {
            if (Array.isArray(x)) {
              x.forEach((y: any) => incomingProductArticleNoArr.push(String(y)));
            } else {
              incomingProductArticleNoArr.push(String(x));
            }
          });
        } else if (incomingProductArticleNoRaw) {
          if (typeof incomingProductArticleNoRaw === 'string' && incomingProductArticleNoRaw.includes(',')) {
            incomingProductArticleNoRaw.split(',').forEach((s: string) => incomingProductArticleNoArr.push(s.trim()));
          } else {
            incomingProductArticleNoArr.push(String(incomingProductArticleNoRaw));
          }
        }
      }

      const returnedIncomingTcId = respData.incomingTcId || respData.incoming_tc_id || respData.incomingId || effectiveIncomingTcId || incomingTcId;

      // If the incoming TC number looks like an ATC (GOTS/ATU prefix), treat it as ATC
      const isAtcValue = (val: any) => {
        if (!val) return false;
        try { return String(val).toUpperCase().startsWith('GOTS') || String(val).toUpperCase().startsWith('ATU'); } catch { return false; }
      };

  const rawIncomingNo = tcInfo.tcProductIncomingTcNo || tcInfo.incomingTcNo || tcInfo.incoming_tc_no || '';
  const rawAtcNo = tcInfo.incomingAtcNo || tcInfo.incoming_atc_no || '';
  const rawRmdfNo = tcInfo.incomingRmdfNo || tcInfo.incoming_rmdf_no || '';

      const licensingCodeRaw =
        tcInfo.incomingTcLicensingCode ||
        '';

  const incomingIsAtc = isAtcValue(rawIncomingNo);
  const incomingIsRmdf = (function(val: any){ if(!val) return false; try { return String(val).toUpperCase().startsWith('RMDF-') || String(val).toUpperCase().startsWith('RMDF'); } catch { return false; } })(rawIncomingNo);

  const tcIncomingATCNo = incomingIsAtc ? String(rawIncomingNo) : (rawAtcNo || '');
  const tcIncomingRMDFNo = incomingIsRmdf ? String(rawIncomingNo) : (rawRmdfNo || '');
  const tcIncomingTCNo = (!incomingIsAtc && !incomingIsRmdf) ? (rawIncomingNo || '') : '';

      const inputTcStandard = derivedInputTcStandard;
      const aggregatedStandards = uniqueStrings(
        inputTcStandard.reduce<string[]>((acc, entry) => {
          if (Array.isArray(entry.incomingProductStandard)) {
            acc.push(...entry.incomingProductStandard);
          }
          return acc;
        }, [])
      );

      return {
        id: 0,
        incomingTcId: returnedIncomingTcId,
        productId: respData.productId || respData.product_id || productId,
        incomingTcInfo: {
          tcAssociatedProductArticleNo: tcInfo.tcAssociatedProductArticleNo || tcInfo.tc_associated_product_article_no || "",
          tcIncomingTCNo: tcIncomingTCNo,
          tcIncomingATCNo: tcIncomingATCNo,
          tcIncomingRMDFNo: tcIncomingRMDFNo,
          incomingTcLicensingCode: licensingCodeRaw,
          tcIncomingTCStandard: aggregatedStandards.length > 0
            ? aggregatedStandards
            : normalizeStringArray(tcInfo.incomingTcStandard || tcInfo.incoming_tc_standard),
          tcIncomingTCStandardDescription: normalizeStringArray(
            tcInfo.incomingTcStandardDescription ||
            tcInfo.incoming_tc_standard_description ||
            aggregatedStandards
          ),
          tcIncomingProductNo: incomingProductArticleNoArr as any,
          tcIncomingTcId: tcInfo.incomingTcNoId || tcInfo.incoming_tc_no_id || returnedIncomingTcId || "",
          tcIncomingAtcId: "",
          tcIncomingLedgerTransactionId: "",
          isFlagSeller: (tcInfo.isSellerFarm === 'Yes' || tcInfo.isSeller === 'Yes' || tcInfo.isFlagSeller === 'Yes') ? 'Yes' : 'No',
        },
        productNetWeight: {
          netWeights,
          rawMaterials,
          standards: standardsByArticle,
        },
        inputTcStandard,
      };
    } catch (err) {
      console.error("Error fetching incoming TC detail:", err);
      throw new Error("Failed to load incoming TC detail.");
    }
  }
  

export async function saveIncomingTcDetail(
    executePost: (url: string, payload: any) => Promise<any>,
    tcId: string,
    incomingDetail: IncomingTcEntry,
    oldProductArticleNo: string,
    oldProductId: string,
    sameIncomingTCNewProductCreate: 'Yes' | 'No'
  ): Promise<boolean> {
    try {
    
      const queryParams = new URLSearchParams({
        oldProductId: oldProductId,
        oldProductArticleNo: oldProductArticleNo,
        sameIncomingTCNewProductCreate: sameIncomingTCNewProductCreate,
      });
  
      const url = `${TCAppConfig.POST_INCOMING_DETAIL_V4}?${queryParams.toString()}`;
  
    
  
      const payload: any = {
        tcId,
        productId: incomingDetail.productId || '',
      };
 
      if (incomingDetail.incomingTcId && sameIncomingTCNewProductCreate === 'No') {
        payload.incomingTcId = incomingDetail.incomingTcId;
      }
  

      const standardsMapFromState = incomingDetail.productNetWeight?.standards || {};
      const fallbackStandards = normalizeStringArray(incomingDetail.incomingTcInfo?.tcIncomingTCStandard || []);

      const netWeightEntries = Object.entries(incomingDetail.productNetWeight.netWeights || {})
        .filter(([_, weight]) => weight && Number(weight) > 0)
        .map(([incomingArticleNo, weight]) => {
          const netWeight = Number(weight);
          const rawMaterials = incomingDetail.productNetWeight.rawMaterials?.[incomingArticleNo] || [];
          const processedRawMaterials = rawMaterials.map((rm: any) => ({
            ...rm,
            rawMaterialPercentage: Math.round(rm.rawMaterialPercentage || 0),
            rawMaterialWeight: Number(((Math.round(rm.rawMaterialPercentage || 0) / 100) * netWeight).toFixed(2)),
          }));

          const standardsFromInput =
            incomingDetail.inputTcStandard?.find((entry) => entry.incomingProductArticleNo === incomingArticleNo)
              ?.incomingProductStandard || [];

          const standardsForArticle = normalizeStringArray(
            standardsMapFromState[incomingArticleNo] || standardsFromInput || fallbackStandards
          );

          return {
            tcProductIncomingArticleNo: incomingArticleNo,
            tcProductIncomingArticleNetWeightUsed: netWeight,
            tcProductIncomingTcStandard: standardsForArticle,
            tcProductIncomingRawMaterialInformation: processedRawMaterials,
          };
        });

      const anyDetail: any = incomingDetail;
      const isFlagFromInfo = anyDetail.incomingTcInfo?.isFlagSeller ?? anyDetail.incomingTcInfo?.isSeller;
      const isFlagTop = anyDetail.isFlagSeller ?? anyDetail.isSeller;
      const isSellerFarm = String(isFlagFromInfo ?? isFlagTop ?? 'No') === 'Yes' ? 'Yes' : 'No';

      const incomingTcNoId =
        incomingDetail.incomingTcInfo?.tcIncomingTcId ||
        incomingDetail.incomingTcId ||
        incomingDetail.incomingTcInfo?.tcIncomingAtcId ||
        incomingDetail.incomingTcInfo?.tcIncomingLedgerTransactionId ||
        '';

      let incomingTcLicensingCode = incomingDetail.incomingTcInfo?.incomingTcLicensingCode || '';
      if (incomingTcLicensingCode && !incomingTcLicensingCode.startsWith('CB-')) {
        incomingTcLicensingCode = `CB-${incomingTcLicensingCode}`;
      }

      if (isSellerFarm === 'Yes') {
        payload.incomingTcProductInformation = [
          {
            tcAssociatedProductArticleNo: incomingDetail.incomingTcInfo.tcAssociatedProductArticleNo,
            tcProductIncomingTcNo: 'na',
            isSellerFarm: 'Yes',
            incomingTcNoId: 'na',
            incomingTcLicensingCode: 'na',
            incomingTcProductNetWeightUsedInformation: [
              {
                tcProductIncomingArticleNo: 'na',
                tcProductIncomingArticleNetWeightUsed: 0,
                tcProductIncomingTcStandard: ['na'],
                tcProductIncomingRawMaterialInformation: [],
              },
            ],
          },
        ];
      } else {
        payload.incomingTcProductInformation = [
          {
            tcAssociatedProductArticleNo: incomingDetail.incomingTcInfo.tcAssociatedProductArticleNo,
            tcProductIncomingTcNo:
              incomingDetail.incomingTcInfo.tcIncomingTCNo ||
              incomingDetail.incomingTcInfo.tcIncomingATCNo ||
              incomingDetail.incomingTcInfo.tcIncomingRMDFNo ||
              '',
            isSellerFarm: 'No',
            incomingTcNoId: incomingTcNoId,
            incomingTcLicensingCode: incomingTcLicensingCode,
            incomingTcProductNetWeightUsedInformation: netWeightEntries,
          },
        ];
      }
  
      
  
      const response = await executePost(url, payload);
  
      if (response?.status === 200) {
        return true;
      }
      
      console.error("Save failed:", response);
      return false;
      
    } catch (err: any) {
      console.error(" Error saving incoming TC detail:", err);
      throw new Error(err?.response?.data?.message || "Failed to save incoming TC detail.");
    }
  }
  
export async function deleteIncomingTcDetail(
    executeDelete: (
      url: string,
      params?: Record<string, any>,
      config?: Record<string, any>
    ) => Promise<any>,
    tcId: string,
    incomingTcId: any,
    incomingTcNo: string,
    productId: any,
    productArticleNo: string
  ): Promise<any> {
    try {
      const params: Record<string, any> = {
        tcId,
        incomingTCId: String(incomingTcId),
        incomingTCNo: incomingTcNo,
        productId,
        productArticleNo,
      };

      const response = await executeDelete(`${TCAppConfig.TC_DELETE_API_V4}`, params);
      return response;
    } catch (err) {
      console.error("Error deleting incoming TC detail:", err);
      throw new Error("Failed to delete incoming TC detail.");
    }
  }
  